const MAX_TEXT_LENGTH = 100;
const SECTION_VIEW_DELAY_MS = 750;

let activeSectionTracker = null;
let analyticsInitialized = false;

function cleanText(value, maxLength = MAX_TEXT_LENGTH) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

function slugify(value) {
  return cleanText(value, 60)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function safeDestination(rawDestination) {
  if (!rawDestination) return undefined;
  if (rawDestination.startsWith("#")) return /^#[a-z0-9_-]+$/i.test(rawDestination) ? rawDestination : undefined;
  if (rawDestination.startsWith("mailto:")) return "mailto";

  try {
    const url = new URL(rawDestination, window.location.origin);
    return window.modeckAnalytics?.safeLocation(url.toString(), false);
  } catch (error) {
    return undefined;
  }
}

export function trackEvent(eventName, parameters = {}) {
  if (typeof window.gtag !== "function") return;

  const cleanParameters = Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

  window.modeckAnalytics?.event(eventName, cleanParameters);
}

function trackPageView(route) {
  window.modeckAnalytics?.page(route.path, route.meta.title || 'MoDeck - Page not found');
}

function clickPlacement(element) {
  const explicitPlacement = element.closest("[data-analytics-placement]")?.dataset.analyticsPlacement;
  if (explicitPlacement) return explicitPlacement;

  const section = element.closest("[data-analytics-section]");
  if (section) return section.dataset.analyticsSection;
  if (element.closest("header")) return "header";
  if (element.closest("footer, .footer-area-wrapper")) return "footer";
  return "page";
}

function trackedElementId(element) {
  const explicitId = element.dataset.analyticsId;
  if (explicitId) return explicitId;
  if (element.id) return element.id;

  const textId = slugify(element.getAttribute("aria-label") || element.textContent);
  if (textId) return textId;

  const destination = safeDestination(element.getAttribute("href"));
  return slugify(destination) || element.tagName.toLowerCase();
}

function handleLandingClick(event) {
  const element = event.target.closest?.("a, button");
  if (!element) return;
  if (element.closest('#modeck-consent-panel')) return;

  const href = element.getAttribute('href') || '';
  if (/^https:\/\/modeck-app\.s3\.amazonaws\.com\/MoDeck_Setup\.(exe|dmg)$/.test(href)) {
    trackEvent('desktop_download', { operating_system: href.endsWith('.exe') ? 'windows' : 'macos' });
  }

  trackEvent("landing_click", {
    element_id: trackedElementId(element),
    element_text: cleanText(element.getAttribute("aria-label") || element.textContent),
    element_type: element.tagName.toLowerCase(),
    placement: clickPlacement(element),
    link_destination: safeDestination(element.getAttribute("href")),
  });
}

function createSectionTracker() {
  const elements = [...document.querySelectorAll("[data-analytics-section]")];
  if (!elements.length || typeof window.IntersectionObserver !== "function") return null;

  const states = new Map(
    elements.map((element, index) => [
      element,
      {
        element,
        id: element.dataset.analyticsSection,
        name: element.dataset.analyticsSectionName || element.dataset.analyticsSection,
        position: index + 1,
        inViewport: false,
        visibleAt: null,
        totalVisibleMs: 0,
        viewTimer: null,
        viewTracked: false,
      },
    ])
  );
  let stopped = false;

  function pause(state) {
    if (state.visibleAt !== null) {
      state.totalVisibleMs += performance.now() - state.visibleAt;
      state.visibleAt = null;
    }
    if (state.viewTimer) {
      window.clearTimeout(state.viewTimer);
      state.viewTimer = null;
    }
  }

  function resume(state) {
    if (state.visibleAt === null && state.inViewport && !document.hidden) {
      state.visibleAt = performance.now();
    }
    if (!state.viewTracked && !state.viewTimer && state.inViewport && !document.hidden) {
      state.viewTimer = window.setTimeout(() => {
        state.viewTimer = null;
        if (!state.inViewport || document.hidden || stopped) return;
        state.viewTracked = true;
        trackEvent("section_view", {
          section_id: state.id,
          section_name: state.name,
          section_position: state.position,
        });
      }, SECTION_VIEW_DELAY_MS);
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const state = states.get(entry.target);
        state.inViewport = entry.isIntersecting;
        if (state.inViewport) resume(state);
        else pause(state);
      });
    },
    { rootMargin: "-25% 0px -25% 0px", threshold: 0 }
  );

  elements.forEach((element) => observer.observe(element));

  function handleVisibilityChange() {
    states.forEach((state) => {
      if (document.hidden) pause(state);
      else resume(state);
    });
  }

  function flushEngagement() {
    states.forEach((state) => {
      pause(state);
      if (state.totalVisibleMs < 1000) return;

      trackEvent("section_engagement", {
        section_id: state.id,
        section_name: state.name,
        section_position: state.position,
        engagement_seconds: Math.round(state.totalVisibleMs / 1000),
        engagement_time_msec: Math.round(state.totalVisibleMs),
        transport_type: "beacon",
      });
      state.totalVisibleMs = 0;
    });
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", flushEngagement);
    flushEngagement();
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flushEngagement);

  return { stop };
}

function resetSectionTracking(route) {
  activeSectionTracker?.stop();
  activeSectionTracker = route.name === "Home" ? createSectionTracker() : null;
}

export function initLandingAnalytics(router) {
  if (analyticsInitialized) return;
  analyticsInitialized = true;

  document.addEventListener("click", handleLandingClick);
  router.afterEach((route, from, failure) => {
    if (failure) return;
    if (from.name && route.path === from.path) return;
    window.setTimeout(() => {
      trackPageView(route);
      resetSectionTracking(route);
    }, 0);
  });
}
